import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Megaphone, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminAnnouncementsPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", release_id: "", announcement_type: "general" });
  const [saving, setSaving] = useState(false);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements?limit=20");
      setAnnouncements(data.announcements);
    } catch { toast.error("Failed to load announcements"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAnnouncements(); }, [fetchAnnouncements]);

  if (!isAdmin) return null;

  const handleCreate = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body required"); return; }
    setSaving(true);
    try {
      await api.post("/announcements", {
        title: form.title.trim(),
        body: form.body.trim(),
        release_id: form.release_id.trim() || null,
        announcement_type: form.announcement_type,
      });
      toast.success("Announcement posted! All users notified.");
      setShowCreate(false);
      setForm({ title: "", body: "", release_id: "", announcement_type: "general" });
      fetchAnnouncements();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await api.delete(`/announcements/${id}`);
      toast.success("Deleted");
      fetchAnnouncements();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="admin-announcements-panel" className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-semibold text-zinc-100 flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-rose-400" /> Manage Announcements
        </h2>
        <Button data-testid="new-announcement-btn" size="sm" onClick={() => setShowCreate(true)} className="bg-rose-600 hover:bg-rose-500 text-white">
          <Plus className="w-3.5 h-3.5 mr-1" /> New
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 text-zinc-500 animate-spin" /></div>
      ) : announcements.length === 0 ? (
        <p className="text-zinc-600 text-sm text-center py-4">No announcements yet</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {announcements.map((a) => (
            <div key={a.announcement_id} className="flex items-center gap-3 p-3 bg-zinc-800/30 rounded-lg border border-zinc-800/50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded-full font-bold ${a.announcement_type === "new_release" ? "bg-rose-500/10 text-rose-400 border border-rose-500/20" : "bg-zinc-700/50 text-zinc-400 border border-zinc-600"}`}>
                    {a.announcement_type === "new_release" ? "New Drop" : "News"}
                  </span>
                  <span className="text-[10px] text-zinc-600">{new Date(a.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm font-medium text-zinc-200 truncate">{a.title}</p>
                <p className="text-xs text-zinc-500 truncate">{a.body}</p>
              </div>
              <button data-testid={`delete-announcement-${a.announcement_id}`} onClick={() => handleDelete(a.announcement_id)} className="p-1.5 text-zinc-500 hover:text-rose-400 hover:bg-zinc-700 rounded transition-colors flex-shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader><DialogTitle className="font-heading">New Announcement</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs">Type</Label>
              <Select value={form.announcement_type} onValueChange={(v) => setForm({ ...form, announcement_type: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-300 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="general">General News</SelectItem>
                  <SelectItem value="new_release">New Release Drop</SelectItem>
                  <SelectItem value="price_alert">Price Alert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Title *</Label>
              <Input data-testid="announcement-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="KHMG-289 just dropped!" className="bg-zinc-950 border-zinc-800 text-zinc-100 mt-1" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Body *</Label>
              <Input data-testid="announcement-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Details about the announcement..." className="bg-zinc-950 border-zinc-800 text-zinc-100 mt-1" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Linked Release ID (optional)</Label>
              <Input value={form.release_id} onChange={(e) => setForm({ ...form, release_id: e.target.value })} placeholder="khmg-289" className="bg-zinc-950 border-zinc-800 text-zinc-100 mt-1" />
              <p className="text-[10px] text-zinc-600 mt-1">Links the announcement to a release page</p>
            </div>
            <Button data-testid="post-announcement-btn" onClick={handleCreate} disabled={saving} className="w-full bg-rose-600 hover:bg-rose-500 text-white">
              {saving ? "Posting..." : "Post Announcement"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
